import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoaderCircle } from 'lucide-react'
import { consumeEntraReturnPath, getLoginConfig, handleEntraRedirect, startEntraLogin } from '../api/entra'
import { fetchCurrentUser } from '../api/auth'
import { useAuthStore } from '../stores/authStore'

interface LoginConfig {
  enabled: boolean
  tenant_id: string | null
  client_id: string | null
  local_password_login: boolean
}

function errorText(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object' && 'message' in detail) return String(detail.message)
    if (!error.response) return 'Unable to connect to the sign-in service. Check your connection and try again.'
  }
  return error instanceof Error ? error.message : 'Sign-in could not be completed. Please try again.'
}

export default function LoginPage() {
  const [config, setConfig] = useState<LoginConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [configFailed, setConfigFailed] = useState(false)
  const [cookieBlocked, setCookieBlocked] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [stage, setStage] = useState<'checking' | 'callback' | 'redirecting'>('checking')
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = (location.state as { from?: string } | null)?.from || '/'

  useEffect(() => {
    let active = true
    const initialize = async () => {
      setLoading(true)
      setConfigFailed(false)
      setCookieBlocked(false)
      setError(null)
      setStage('checking')
      let configLoaded = false
      try {
        const resolved = await getLoginConfig()
        if (!active) return
        configLoaded = true
        setConfig(resolved)
        if (resolved.enabled) {
          setStage('callback')
          const user = await handleEntraRedirect(resolved)
          if (user && active) {
            // The Microsoft sign-in succeeded and the backend set a session
            // cookie. In a cross-site deployment (frontend and API on different
            // domains) that cookie is SameSite=None; browsers that block
            // third-party cookies — notably Incognito/Private windows — accept
            // it from this response but then refuse to SEND it on the next
            // request. Entering the app in that state leaves every API call
            // 401-ing and bounces the user straight back here (a blank page and
            // an endless re-login loop). So confirm the cookie actually
            // round-trips before navigating in. /auth/me is an auth endpoint,
            // so the client's 401 interceptor does not try to refresh/logout on
            // it — a blocked cookie surfaces here as a clean 401.
            try {
              await fetchCurrentUser()
            } catch (verifyError) {
              if (
                verifyError instanceof AxiosError &&
                verifyError.response?.status === 401
              ) {
                useAuthStore.getState().logout()
                if (active) {
                  setCookieBlocked(true)
                  setLoading(false)
                }
                return
              }
              throw verifyError
            }
            navigate(consumeEntraReturnPath(), { replace: true })
            return
          }
        }
        if (active && useAuthStore.getState().isAuthenticated) {
          navigate(consumeEntraReturnPath(), { replace: true })
        }
        if (window.location.search.includes('signed_out=1')) {
          window.history.replaceState({}, '', '/login')
        }
      } catch (cause) {
        if (active) {
          setError(errorText(cause))
          setConfigFailed(!configLoaded)
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    void initialize()
    return () => { active = false }
  }, [navigate, retryCount])

  const handleMicrosoftLogin = async (chooseAccount = false) => {
    setLoading(true)
    setStage('redirecting')
    setError(null)
    try {
      await startEntraLogin(returnTo, config ?? undefined, chooseAccount)
    } catch (cause) {
      setError(errorText(cause))
      setLoading(false)
    }
  }

  const microsoftEnabled = config?.enabled === true

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-900 px-4 py-10">
      <section className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Protocol Decompiler</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Sign in to continue</p>
        </div>

        {loading && !config ? (
          <p className="flex items-center justify-center gap-2 py-3 text-sm text-gray-600 dark:text-gray-300" role="status">
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            {stage === 'callback' ? 'Finishing Microsoft sign-in…' : 'Connecting to sign-in service…'}
          </p>
        ) : cookieBlocked ? (
          <div className="space-y-4">
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200" role="alert">
              <p className="font-medium">Your browser is blocking the sign-in cookie.</p>
              <p className="mt-1">
                Sign-in with Microsoft worked, but this browser isn’t letting the app keep you
                signed in. This usually happens in an Incognito/Private window, or when
                third-party cookies are turned off.
              </p>
              <p className="mt-2">To continue, either:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                <li>open the app in a normal (non-incognito) window, or</li>
                <li>allow cookies for this site in your browser settings, then try again.</li>
              </ul>
            </div>
            <button type="button" onClick={() => setRetryCount((count) => count + 1)} className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              Try again
            </button>
          </div>
        ) : configFailed ? (
          <div className="space-y-3">
            <button type="button" onClick={() => setRetryCount((count) => count + 1)} className="w-full rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              Try again
            </button>
          </div>
        ) : microsoftEnabled ? (
          <div className="space-y-4">
            <button type="button" onClick={() => handleMicrosoftLogin()} disabled={loading} className="flex w-full items-center justify-center gap-3 rounded-md bg-[#2563eb] px-4 py-3 font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60">
              {loading ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <MicrosoftMark />}
              {loading ? (stage === 'redirecting' ? 'Redirecting to Microsoft…' : 'Finishing sign-in…') : 'Continue with Microsoft'}
            </button>
            <button type="button" onClick={() => handleMicrosoftLogin(true)} disabled={loading} className="w-full text-center text-sm text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-300">
              Use another Microsoft account
            </button>
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">Your organization’s Microsoft session will be reused when available.</p>
          </div>
        ) : (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
            Microsoft sign-in is not configured for this deployment. Contact the application administrator.
          </p>
        )}

        {error && <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" role="alert">{error}</p>}
        <p className="mt-7 text-center text-xs text-gray-500 dark:text-gray-400">
          New accounts receive Viewer access. An administrator can grant additional permissions.
        </p>
      </section>
    </main>
  )
}

function MicrosoftMark() {
  return (
    <span className="grid h-4 w-4 grid-cols-2 gap-[2px]" aria-hidden="true">
      <span className="bg-red-500" /><span className="bg-green-500" />
      <span className="bg-blue-500" /><span className="bg-yellow-400" />
    </span>
  )
}
